import { useState } from 'react';
import type { ThemeReferenceFileRecord } from '@/lib/themeReferenceFiles';
import { parseReferencePages } from '@/lib/themeReferenceFiles';
import { convertToZifekTags, contentNeedsCorrection } from '@/lib/themeTagConverter';

interface ReferenceFileModalProps {
  initial?: ThemeReferenceFileRecord | null;
  /** Contenu pré-rempli pour une création (ex. import d'un fichier .md). */
  seedDraft?: { titre: string; description: string; contenu: string } | null;
  onClose: () => void;
  onSave: (input: { titre: string; description: string; contenu: string }) => Promise<void>;
}

const CONTENT_TEMPLATE = `# Instructions générales
Décris ici le ton, la palette, la typographie, l'ambiance attendue...

# Pages
- home : Page d'accueil
- produits : Boutique / Produits
- apropos : À propos
- contact : Contact

# Sections obligatoires
- Hero avec image de fond
- Bloc produits mis en avant
- Avis clients
- Newsletter

# Notes techniques
Les balises du moteur (ex. {{site_name}}, {{products}}, {{footer}}) sont autorisées.`;

export default function ReferenceFileModal({ initial, seedDraft, onClose, onSave }: ReferenceFileModalProps) {
  const [titre, setTitre] = useState(initial?.titre || seedDraft?.titre || '');
  const [description, setDescription] = useState(initial?.description || seedDraft?.description || '');
  const [contenu, setContenu] = useState(initial?.contenu || seedDraft?.contenu || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [correctionNote, setCorrectionNote] = useState('');

  const detectedPages = parseReferencePages(contenu);
  const hasCorrectionNeeded = contentNeedsCorrection(contenu);

  /** Remplace toutes les balises non-Zifek par les balises Zifek (table de conversion). */
  const handleCorrect = () => {
    const result = convertToZifekTags(contenu);
    if (result.detected === 0) {
      setCorrectionNote('Aucune balise non-Zifek détectée — le fichier utilise déjà les balises Zifek.');
      return;
    }
    setContenu(result.content);
    setCorrectionNote(`Correction effectuée : ${result.replaced} balise(s) remplacée(s), ${result.removed} supprimée(s) sur ${result.detected} détectée(s).`);
  };

  const handleSubmit = async () => {
    if (!titre.trim()) {
      setError('Le titre est obligatoire.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave({ titre, description, contenu });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur d\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-50" onClick={saving ? undefined : onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4">
        <div className="w-full max-w-2xl max-h-[92vh] bg-background-50 rounded-lg border border-background-200/70 shadow-xl flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-background-200/70 flex-shrink-0">
            <div className="flex items-center gap-2">
              <i className="ri-book-open-line text-foreground-500"></i>
              <h3 className="text-base font-semibold text-foreground-950">
                {initial ? 'Modifier le fichier de référence' : 'Nouveau fichier de référence'}
              </h3>
            </div>
            <button
              onClick={onClose}
              disabled={saving}
              className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-40"
            >
              <i className="ri-close-line text-foreground-500"></i>
            </button>
          </div>

          {/* Body */}
          <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
                Titre <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={titre}
                onChange={(e) => { setTitre(e.target.value); setError(''); }}
                placeholder="Ex. Référence — Marketplace beauté"
                autoFocus
                className="w-full h-10 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="À quoi sert ce fichier de référence ?"
                rows={2}
                className="w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 resize-none"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold text-foreground-600">Contenu (instructions pour l&apos;IA)</label>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleCorrect}
                    className={`text-[11px] font-medium cursor-pointer whitespace-nowrap flex items-center gap-1 ${
                      hasCorrectionNeeded ? 'text-amber-600 hover:text-amber-700' : 'text-foreground-400 hover:text-foreground-600'
                    }`}
                    title="Convertir les balises non-Zifek (merchant., #bloc, {% %}) vers les balises Zifek officielles"
                  >
                    <i className="ri-magic-line"></i>
                    Corriger les balises
                  </button>
                  <button
                    onClick={() => setContenu(CONTENT_TEMPLATE)}
                    className="text-[11px] text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap flex items-center gap-1"
                  >
                    <i className="ri-file-copy-line"></i>
                    Insérer un modèle
                  </button>
                </div>
              </div>
              <textarea
                value={contenu}
                onChange={(e) => setContenu(e.target.value)}
                placeholder={CONTENT_TEMPLATE}
                rows={14}
                spellCheck={false}
                className="w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-100 text-xs text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 resize-none font-mono leading-relaxed"
                style={{ tabSize: 2 }}
              />
              <div className="flex items-center justify-between mt-1.5">
                <p className="text-[10px] text-foreground-400">
                  Ajoute une section <strong className="text-foreground-500">« Pages »</strong> (lignes <code className="font-mono bg-background-100 px-1 rounded">- clé : Libellé</code>) pour définir les pages générées.
                </p>
                <span className="text-[10px] text-foreground-400 font-mono">{contenu.length} car.</span>
              </div>
              {hasCorrectionNeeded && !correctionNote && (
                <p className="text-[10px] text-amber-600 mt-1.5 flex items-center gap-1">
                  <i className="ri-alert-line"></i>
                  Des balises non-Zifek ont été détectées. Clique sur « Corriger les balises » pour les convertir.
                </p>
              )}
              {correctionNote && (
                <p className="text-[10px] text-accent-700 mt-1.5 flex items-center gap-1 bg-accent-50 border border-accent-100 rounded-md px-2 py-1.5">
                  <i className="ri-check-line"></i>
                  {correctionNote}
                </p>
              )}
            </div>

            {/* Pages détectées */}
            <div className={`p-3 rounded-lg border ${detectedPages.length > 0 ? 'border-accent-200/60 bg-accent-50/50' : 'border-background-200/70 bg-background-100/50'}`}>
              <div className="flex items-center gap-2 mb-2">
                <i className={`${detectedPages.length > 0 ? 'ri-list-check-2 text-accent-600' : 'ri-information-line text-foreground-400'} text-sm`}></i>
                <span className="text-[11px] font-semibold text-foreground-700">
                  {detectedPages.length > 0
                    ? `${detectedPages.length} page(s) détectée(s) dans le contenu`
                    : 'Aucune page détectée — la liste complète par défaut sera utilisée'}
                </span>
              </div>
              {detectedPages.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {detectedPages.map((p) => (
                    <span key={p.key} className="inline-flex items-center gap-1 text-[10px] bg-background-50 border border-background-200/70 px-2 py-0.5 rounded-full text-foreground-600">
                      <span className="font-mono text-foreground-400">{p.key}</span>
                      {p.label}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {error && (
              <div className="px-3 py-2.5 rounded-md bg-red-50 border border-red-100 flex items-start gap-2">
                <i className="ri-error-warning-line text-red-500 text-sm flex-shrink-0 mt-0.5"></i>
                <span className="text-xs text-red-700">{error}</span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-5 py-3.5 border-t border-background-200/70 flex-shrink-0">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40"
            >
              Annuler
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving || !titre.trim()}
              className="px-5 py-2 rounded-full text-sm font-semibold bg-foreground-950 text-background-50 whitespace-nowrap hover:bg-foreground-800 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? (
                <><i className="ri-loader-4-line animate-spin text-xs"></i>Enregistrement...</>
              ) : (
                <><i className="ri-save-line"></i>{initial ? 'Enregistrer' : 'Créer le fichier'}</>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}